import React, { useState, useEffect, useRef, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

// mui
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

// component
import TopBackButton from '@/component/common/top-back-button';
import SFAlert from '@/component/common/sf-alert';
import SendLinkModal from '@/component/wallet/send-link-modal';
import networkList, { resNetworkList } from '@/component/wallet/NetworkList';
// import Balance from '@/component/balance';
import Balance from '@/component/web3auth/Balance';
import { useChainId } from 'wagmi';
import { useSwitchChain, useWeb3Auth } from '@web3auth/modal/react';
// hooks
import { useWeb3 } from '@/hooks/use-web3';
// lib & store
import { SFPalette } from '@/lib/color';
import {
  useSendInfo,
  useOutLinkInfo,
  useSendInfoActions,
  useOutLinkInfoActions,
  SendState,
} from '@/store/zustand/store';
import { hexaDeciamlToUtf } from '@/lib/Modules';
import { compareNum, fiexdDecimal, NumWithComma } from '@/lib/BigNumbers';
import { ContractBalanceOf, RESULT_SMALL } from '@/types';
import { METHOD_BALANCE_OF } from '@/lib/common/name-space';
import { weiToEth } from '@/lib/web3/utils';
import { bioLocalStorage, localGetStorage } from '@/lib/storage';
// hook
// /**
//  * @description Util
//  * @returns
//  */
// const formatToKoreanCurrency = (_value: number) => {
//   const value = _value.toString();
//   const numberValue = parseInt(value.replace(/,/g, ''), 10); // 콤마를 제거하고 숫자로 변환합니다.
//   return isNaN(numberValue) ? '' : numberValue.toLocaleString('ko-KR'); // 숫자가 아니라면 빈 문자열을 반환하고, 숫자라면 한국의 숫자 형식으로 변환합니다.
// };

export default function SendToken() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setSendInfo } = useSendInfoActions();
  const { setOutLinkInfo } = useOutLinkInfoActions();
  const [openModal, setOpenModal] = useState(false);
  const [value, setValue] = useState('');
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [network, setNetwork] = useState<resNetworkList | null>(null);
  const sendInfo = useSendInfo();
  const outLinkInfo = useOutLinkInfo();
  const toAddr = sendInfo.to.substring(2, sendInfo.to.length);
  const { web3authCall } = useWeb3();
  const address = useRef('');
  const chainId = useRef(0);
  const currentChainId = useChainId();
  const networks = useRef<resNetworkList[] | null>(null);

  // 결제 요청 모드 관련 상태
  const [isFromPaymentRequest, setIsFromPaymentRequest] = useState(false);
  const { switchChain, loading: switchChainLoading } = useSwitchChain();
  const { web3Auth } = useWeb3Auth();
  const paymentRequestProcessed = useRef(false);
  // useEffect(() => {
  //   if (!chain) {
  //     return;
  //   }
  //   if (!address) {
  //     navigate('/wallet');
  //     return;
  //   }
  //   if (!sendInfo.to) {
  //     navigate('/sendForm');
  //     return;
  //   }

  //   return () => {};
  // }, [chain, address, sendInfo, navigate]);

  // 결제 요청 자동 처리 로직
  useEffect(() => {
    // 이미 처리했거나 결제 요청 정보가 없으면 스킵
    if (paymentRequestProcessed.current) return;
    if (!outLinkInfo.amount || !outLinkInfo.symbol || !outLinkInfo.chain) return;
    if (!web3authCall?.connected()) return;

    paymentRequestProcessed.current = true;
    setIsFromPaymentRequest(true);

    (async () => {
      const targetChainId = Number(outLinkInfo.chain);

      // 1. 현재 체인 확인 및 전환
      const currentChainHex = web3Auth?.currentChain?.chainId;
      const currentChainDecimal = currentChainHex ? parseInt(currentChainHex, 16) : 0;
      console.log(`currentChainDecimal: ${currentChainDecimal}`);
      console.log(`targetChainId: ${targetChainId}`);
      if (targetChainId !== currentChainDecimal) {
        try {
          const hexChainId = `0x${targetChainId.toString(16)}`;
          await switchChain(hexChainId);
          toast.success('네트워크가 변경되었습니다.');
        } catch (error) {
          console.error('체인 전환 실패:', error);
          toast.error(`${t('toast.checkNetwork')}`);
          return;
        }
      }

      // 2. 대상 체인의 토큰 목록 로드
      const tokenList = await networkList(targetChainId);
      networks.current = tokenList;

      // 3. 매칭되는 토큰 찾기 및 선택
      const matchingToken = tokenList.find((token) => token.symbol.toUpperCase() === outLinkInfo.symbol.toUpperCase());

      if (matchingToken) {
        setNetwork(matchingToken);
      } else {
        toast.error(`${outLinkInfo.symbol} 토큰을 찾을 수 없습니다.`);
        if (tokenList.length > 0) {
          setNetwork(tokenList[0]);
        }
      }

      // 4. 금액 자동 입력 (wei -> ether 변환)
      try {
        const amountInEther = weiToEth(outLinkInfo.amount);
        setValue(amountInEther);
      } catch (error) {
        console.error('금액 변환 오류:', error);
        setValue(outLinkInfo.amount);
      }
    })();

    return () => {};
  }, [outLinkInfo.amount, outLinkInfo.symbol, outLinkInfo.chain, web3authCall, web3Auth, switchChain, t]);
  useEffect(() => {
    if (!network) {
      // TODO 뒤로 돌아가기
    }

    return () => {};
  }, [network]);

  useEffect(() => {
    if (!web3authCall?.connected()) {
      return;
    }
    // 결제 요청 모드이면 여기서 토큰 설정하지 않음 (결제 요청 useEffect에서 처리)
    if (outLinkInfo.amount && outLinkInfo.symbol && outLinkInfo.chain) {
      (async () => {
        address.current = await web3authCall.getAddress();
      })();
      return;
    }

    (async () => {
      const re: any = web3authCall?.getProvider();
      if (!re) {
        return;
      }
      address.current = await web3authCall.getAddress();
      const res: resNetworkList[] = await networkList(currentChainId);
      networks.current = res;
      setNetwork(res[0]);
    })();

    return () => {};
  }, [outLinkInfo.amount, outLinkInfo.symbol, outLinkInfo.chain]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (coin: resNetworkList) => {
    setNetwork(coin);
    handleMenuClose();
  };

  const handleSubmit = async () => {
    console.log(`handleSubmit`);
    console.log(network);
    let balance = null;
    if (network?.token) {
      // erc20 종류
      const payload = {
        method: METHOD_BALANCE_OF,
        symbol: network.symbol,
        address: address.current,
      };
      const result = await web3authCall?.getRpc().contractCall<ContractBalanceOf>(payload);
      balance = NumWithComma(fiexdDecimal(weiToEth(result)));
      // 기반 토큰 체크
      const base = await web3authCall!.getRpc().getBalance(address.current);
      if (base === '0') {
        toast.error('기반코인에 잔액이 있어야 전송가능합니다.');
        return;
      }
    } else {
      balance = await web3authCall!.getRpc().getBalance(address.current);
    }
    if (RESULT_SMALL === compareNum(balance, value)) {
      toast.error('전송수량이 보유수량보다 많습니다.');
      return;
    }
    setSendForm();

    if (!network) {
      return;
    }
    // SNS 유저 + 생체인증 등록 여부 확인
    const snsAddress = localGetStorage('snsAddress');
    const bioCheck = bioLocalStorage();

    if (snsAddress && bioCheck) {
      // SNS 유저이고 생체인증 등록됨 -> 생체인증 화면으로
      navigate('/bioAuthComponent', {
        state: {
          title: `${network.symbol} ${t('title.checkSendPinNum')}`,
          returnUrl: '/wallet',
          options: {
            coin: network.symbol,
            contractAddress: network.token ? network.token : '',
          },
        },
      });
    } else {
      // 그 외 -> PIN 입력 화면으로
      navigate('/pinComponent', {
        state: {
          pinType: 'confirm',
          title: `${network.symbol} ${t('title.checkSendPinNum')}`,
          returnUrl: '/wallet',
          options: {
            coin: network.symbol,
            contractAddress: network.token ? network.token : '',
          },
        },
      });
    }

    // 결제 요청 정보 초기화
    if (isFromPaymentRequest) {
      setOutLinkInfo({
        path: '',
        toAddr: '',
        amount: '',
        symbol: '',
        chain: '',
      });
    }
  };
  const otherNetwork = () => {
    setSendForm();
  };
  const taycanNetwork = () => {
    setSendForm();

    // if (coin === 'TRI') {
    //   sendTri();
    // } else if (coin === 'SFL') {
    //   sendSFL();
    // }
  };
  // const sendTri = () => {
  //   // write?.();
  // };
  // const sendSFL = () => {
  //   const payload: SendState = {
  //     from: address,
  //     to: `0x${toAddr}`,
  //     value,
  //   };
  //   dispatch(setSendInfo(payload));

  // };
  // const setSend = () => {};
  const setSendForm = () => {
    const payload: SendState = {
      from: address.current,
      to: `0x${toAddr}`,
      value,
    };
    setSendInfo(payload);
  };

  return (
    <>
      <Container
        maxWidth="sm"
        sx={{
          minHeight: '812px',
          height: '100vh',
          position: 'relative',
        }}
      >
        <TopBackButton
          title={t('title.amount')}
          subtitle={
            <Stack direction="row" alignItems="center" justifyContent="center" gap={0.5}>
              <Box borderRadius="50%" width={6} height={6} bgcolor={SFPalette.lightGrey2} />
              {/* <Typography color={SFPalette.lightGrey2}>{chain!.name}</Typography> */}
            </Stack>
          }
          boxProps={{
            mb: isFromPaymentRequest ? 2 : 4,
          }}
        />
        {/* 결제 요청 모드 안내 배너 */}
        {isFromPaymentRequest && (
          <Box
            sx={{
              bgcolor: 'primary.main',
              p: 1.5,
              borderRadius: 2,
              mb: 2,
            }}
          >
            <Typography variant="body2" color="white" textAlign="center">
              결제 요청 정보가 적용되었습니다
            </Typography>
          </Box>
        )}
        <Box minHeight={100}>
          <SFAlert
            title={network?.symbol ?? 'unknown'}
            icon={
              <>
                <IconButton
                  onClick={handleMenuOpen}
                  disabled={isFromPaymentRequest}
                  sx={{
                    p: 0,
                  }}
                >
                  <ExpandMoreIcon
                    sx={{
                      color: isFromPaymentRequest ? SFPalette.grey2 : 'white',
                    }}
                  />
                </IconButton>
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                  {networks.current &&
                    networks.current.map((item: resNetworkList, index: number) => (
                      <div key={index}>
                        <MenuItem onClick={() => handleMenuItemClick(item)}>{item.symbol}</MenuItem>
                      </div>
                    ))}
                </Menu>
              </>
            }
          >
            <Box width="50%" mx="auto">
              <TextField
                variant="standard"
                // type="number"
                placeholder="0"
                onChange={(e) => {
                  if (!isFromPaymentRequest) {
                    setValue(e.target.value);
                  }
                }}
                value={value}
                InputProps={{
                  readOnly: isFromPaymentRequest,
                  sx: {
                    my: 2.5,
                    '& .MuiInput-input': {
                      textAlign: 'center',
                      color: isFromPaymentRequest ? SFPalette.grey2 : 'inherit',
                    },
                    fontSize: '2.25rem',
                    fontWeight: 700,
                  },
                }}
              />
            </Box>
            <Divider />
            <Stack direction="row" justifyContent="center" alignItems="center" gap={1.5}>
              {/* 전환 비율 설정(ticker) */}
              {/* <Box>
                <Typography variant="body1" textAlign="center" my={2.5} color={SFPalette.lightGrey2}>
                  {'$ 0.05 (전환)'}
                </Typography>
              </Box>
              <Box width="1px" height="8px" bgcolor={SFPalette.lightGrey1} /> */}
              <Box>
                <Typography component="div" variant="body1" textAlign="center" my={2.5} color={SFPalette.lightGrey2}>
                  {/* {network && <Balance symbol={network.symbol} chainId={chainId.current} token={network.token} />} */}
                  {/* <Balance address={address} chainId={Number(chain!.id)} token={network?.token} /> */}
                  <Balance token={network?.token as `0x${string}`} />
                </Typography>
              </Box>
            </Stack>
          </SFAlert>
        </Box>
        <Box position="absolute" bottom={400} left={0} width="100%">
          <Container maxWidth="sm">
            <Stack direction="row" gap={1} mb={3}>
              <Button
                variant="outlined"
                size="large"
                fullWidth
                onClick={() => {
                  navigate('/wallet');
                }}
              >
                {t('button.previous')}
              </Button>
              <Button
                disabled={value === '0'}
                variant="contained"
                size="large"
                fullWidth
                onClick={async () => {
                  await handleSubmit();
                }}
                // onClick={() => {
                //   // setOpenModal(true);
                // }}
              >
                {t('button.next')}
              </Button>
            </Stack>
          </Container>
        </Box>
        <SendLinkModal
          open={openModal}
          onClose={() => {
            setOpenModal(false);
          }}
        />
      </Container>
      {/* {isSend && <BaseCoin />} */}
      {/* {isSend && (coin === COIN_NAME_TRI ? <Erc20Token contractAddress={TRI_CONTRACT} /> : <BaseCoin />)} */}
    </>
  );
}
